import { useEffect, useState } from "react";
// import { format } from "date-fns";
import { toZonedTime, format as formatTz } from "date-fns-tz";

import { useGlobalStateStore } from "../stores/store";
import { BetWindow } from "./match/BetWindow";

function extendOddsArray(arr) {
  let numbers = [...new Set(arr.map(Number))].sort((a, b) => a - b);

  if (numbers.length === 1) {
    const val = numbers[0];
    if (val <= 1.5) numbers = [val, val + 1, val + 2];
    else if (val >= 5.5) numbers = [val - 2, val - 1, val];
    else numbers = [val - 1, val, val + 1];
  }

  if (numbers.length === 2) {
    const [a, b] = numbers;
    const diff = b - a;

    if (diff > 1) {
      const middle = a + 1;
      numbers = [a, middle, b];
    } else {
      const before = a - 1;
      const after = b + 1;
      if (before >= 0.5) numbers.unshift(before);
      else if (after <= 5.5) numbers.push(after);
    }
  }

  return numbers.slice(0, 3).map((n) => n.toFixed(1));
}

// Converts decimal odds (like 1.5) to fractional odds (like "1/2")
function decimalToFraction(decimal) {
  if (!decimal || decimal === "X" || decimal === "x") return decimal;

  const decNum = typeof decimal === "string" ? parseFloat(decimal) : decimal;
  if (isNaN(decNum) || decNum <= 1) return decimal;

  const value = decNum - 1;
  const precision = 100000; // high precision to catch exact fractions

  let numerator = Math.round(value * precision);
  let denominator = precision;

  // Reduce fraction
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(numerator, denominator);
  numerator /= divisor;
  denominator /= divisor;

  return `${numerator}/${denominator}`;
}
function Footballmatches({ match, onRendered }) {
  useEffect(() => {
    if (onRendered) {
      onRendered(); // Call after render
    }
  }, [onRendered]);

  const store = useGlobalStateStore(); // make sure this hook exists and is correct
  // const { match } = props;
  const [theme, setTheme] = useState("dark");

  const sortedOverGoals = store.filter.markets.over_goals.sort();
  const sortedUnderGoals = store.filter.markets.under_goals.sort();

  function parseUtcOffsetString(offsetStr) {
    const match = offsetStr.match(/UTC([+-]\d+)/);
    if (!match) return "UTC";
    const offset = parseInt(match[1]);
    const ianaOffset = -offset; // reverse the sign: UTC-2 => GMT+2
    return `Etc/GMT${ianaOffset >= 0 ? `+${ianaOffset}` : ianaOffset}`;
  }

  // Read from localStorage and convert to IANA format
  const offsetStr = localStorage.getItem("selectedTimeZone") || "UTC";
  const userTimeZone = parseUtcOffsetString(offsetStr);

  const dateInUTC = new Date(match.fixture_date); // ISO UTC date
  const zonedDate = toZonedTime(dateInUTC, userTimeZone);
  const formattedDate = formatTz(zonedDate, "EEE, dd MMM yyyy, hh:mm a", {
    timeZone: userTimeZone,
  });

  function normalizeOdds(oddsValues) {
    const sortedInputOverValues = localStorage.getItem("overGoals") || "[]";
    const sortedInputUnderValues = localStorage.getItem("underGoals") || "[]";

    const latestSortedOverGoals =
      sortedInputOverValues.length > 0
        ? JSON.parse(sortedInputOverValues)
        : sortedOverGoals;

    const latestSortedUnderGoals =
      sortedInputUnderValues.length > 0
        ? JSON.parse(sortedInputUnderValues)
        : sortedUnderGoals;

    const masterOrder = ["0.5", "1.5", "2.5", "3.5", "4.5", "5.5"];
    const nameParts = oddsValues[0]?.value.split(" ") || [];
    const marketType = nameParts[0]; // "Over", "Under", "Double", etc.

    // Handle Double Chance market
    if (marketType === "Double" || oddsValues[0]?.value.includes("/")) {
      const possibleValues = ["Home/Draw", "Home/Away", "Draw/Away"];
      return possibleValues.map((value) => {
        const found = oddsValues.find((item) => item.value === value);
        return found ? found : { value, odd: "X" };
      });
    }

    if (
      oddsValues.length === 3 &&
      marketType !== "Over" &&
      marketType !== "Under"
    ) {
      return oddsValues.slice(0, 3);
    }

    if (marketType === "Over" || marketType === "Under") {
      const relevantGoals =
        marketType === "Over" ? latestSortedOverGoals : latestSortedUnderGoals;

      const sortedRelevant = [...relevantGoals]
        .filter((val) => masterOrder.includes(val))
        .sort((a, b) => masterOrder.indexOf(a) - masterOrder.indexOf(b));

      const displaySlots = [null, null, null];

      if (sortedRelevant.length === 3) {
        displaySlots[0] = sortedRelevant[0];
        displaySlots[1] = sortedRelevant[1];
        displaySlots[2] = sortedRelevant[2];
      } else if (sortedRelevant.length === 2) {
        const idx1 = masterOrder.indexOf(sortedRelevant[0]);
        const idx2 = masterOrder.indexOf(sortedRelevant[1]);
        const lower = Math.min(idx1, idx2);
        const upper = Math.max(idx1, idx2);

        const hasValueBetween = upper - lower > 1;

        if (lower === 0 && upper === 1) {
          displaySlots[0] = sortedRelevant[0];
          displaySlots[1] = sortedRelevant[1];
        } else if (lower === 4 && upper === 5) {
          displaySlots[1] = sortedRelevant[0];
          displaySlots[2] = sortedRelevant[1];
        } else if (hasValueBetween) {
          displaySlots[0] = sortedRelevant[0];
          displaySlots[2] = sortedRelevant[1];
        } else {
          displaySlots[0] = sortedRelevant[0];
          displaySlots[1] = sortedRelevant[1];
        }
      } else if (sortedRelevant.length === 1) {
        const idx = masterOrder.indexOf(sortedRelevant[0]);
        if (idx <= 2) {
          displaySlots[0] = sortedRelevant[0];
        } else if (idx <= 4) {
          displaySlots[1] = sortedRelevant[0];
        } else {
          displaySlots[2] = sortedRelevant[0];
        }
      }

      return displaySlots.map((goal) => {
        if (!goal) return { value: "X", odd: "X" };
        const label = `${marketType} ${goal}`;
        const found = oddsValues.find((item) => item.value === label);
        return found ? found : { value: label, odd: "X" };
      });
    }

    // Home / Draw / Away fallback
    const possibleValues = ["Home", "Draw", "Away"];
    const existingValues = oddsValues.map((item) => item.value);
    const filled = possibleValues.map((value) => {
      return existingValues.includes(value)
        ? oddsValues.find((item) => item.value === value)
        : { value, odd: "x" };
    });
    return filled.slice(0, 3);
  }

  useEffect(() => {
    // Debounce function to limit how often updateCompetitionBlocks runs
    let resizeTimeout;

    const updateCompetitionBlocks = () => {
      // Clear any existing images first to prevent duplicates
      const competitions = document.querySelectorAll(".competition");
      competitions.forEach((competition) => {
        const emblems = competition.querySelector(".emblems");
        const teamNames = competition.querySelectorAll(
          ".competition-data__country .team-name"
        );

        teamNames.forEach((teamName) => {
          const existingImage = teamName.parentNode.querySelector("img");
          if (existingImage) {
            existingImage.remove();
          }
        });

        if (window.innerWidth <= 575) {
          if (emblems && teamNames.length >= 2) {
            const images = emblems.querySelectorAll("img");
            if (images.length >= 2) {
              const firstImage = images[0].cloneNode(true);
              const secondImage = images[1].cloneNode(true);
              teamNames[0].parentNode.insertBefore(firstImage, teamNames[0]);
              teamNames[1].parentNode.insertBefore(secondImage, teamNames[1]);
            }
          }
        }
      });
    };

    // Debounced resize handler
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateCompetitionBlocks();
      }, 150); // 150ms debounce
    };
    // Initial call
    updateCompetitionBlocks();

    // Add debounced event listener
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  useEffect(() => {
    const currentTheme = document.documentElement.getAttribute("data-theme");
    if (currentTheme) {
      setTheme(currentTheme);
    }
    const observer = new MutationObserver(() => {
      const updatedTheme = document.documentElement.getAttribute("data-theme");
      setTheme(updatedTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleSelectedBet = useGlobalStateStore(
    (state) => state.toggleSelectedBet
  );
  const selectedBet = useGlobalStateStore((state) => state.selectedBet);
  const onBetSelected = (element) => {
    toggleSelectedBet({
      marketId: match.market_id,
      fixtureId: match.fixture_id,
      bookmakerId: match.bookmaker_id,
      elementValue: element.value,
    });
  };
  const isElementSelected = (element) => {
    if (selectedBet == null) return false;
    if (selectedBet.marketId !== match.market_id) return false;
    if (selectedBet.fixtureId !== match.fixture_id) return false;
    if (selectedBet.bookmakerId !== match.bookmaker_id) return false;
    if (selectedBet.elementValue !== element.value) return false;

    return true;
  };

  // Get the saved odds format from localStorage ('decimal' or 'fractional')
  const selectedOddsFormat = localStorage.getItem("selectedOdds") || "decimal";

  // Normalize odds and convert odds to fractional if needed
  const displayOdds = normalizeOdds(match.odds_values).map((element) => {
    if (selectedOddsFormat === "fractional") {
      return {
        ...element,
        odd:
          element.odd && element.odd !== "X" && element.odd !== "x"
            ? decimalToFraction(element.odd)
            : element.odd,
      };
    }
    // decimal default
    return element;
  });

  return (
    <>
      <div className="football-matches__item">
        <div className="site-title__wrap">
          <img
            className="site-title"
            src={
              theme === "light"
                ? match.bookmaker_logo_light
                : match.bookmaker_logo_dark
            }
            alt="icon"
          />
        </div>
        <div className="competition">
          <div className="emblems">
            <img src={match.home_team_logo} alt="emblem" />
            <img src={match.away_team_logo} alt="emblem" />
          </div>
          <div className="competition__data">
            <div className="competition-data__country">
              <span className="team-name-wrap">
                <span className="team-name">{match.home_team_name}</span>
              </span>
              <span className="dash"> — </span>
              <span className="team-name-wrap">
                <span className="team-name">{match.away_team_name}</span>
              </span>
            </div>
            <time dateTime={formattedDate}>{formattedDate}</time>
          </div>
        </div>
        <div className="bet">
          <img
            src={
              theme === "light"
                ? match.market_logo_light
                : match.market_logo_dark
            }
            alt="icon"
          />
        </div>
        <div className="bet-window__wrap">
          {displayOdds.map((element, index) => (
            <BetWindow
              key={index}
              element={element}
              isSelected={isElementSelected(element)}
              onClicked={onBetSelected}
              oddsFormat={selectedOddsFormat}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export default Footballmatches;
